import React from 'react';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, Subject, from, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, zip, takeUntil } from 'rxjs/operators';
import { Spinner } from '@spinnaker/core';
import { DinghyEventsService } from './service/dinghyEvents.service';
import { DinghyEventsTable } from './dinghyEvents.table';
import { PaginationControls } from './paginationControls';
import { SelectCallback } from 'react-bootstrap';
import SplitterLayout from 'react-splitter-layout';
import './index.css';
import { DinghyEventDetail } from './details/dinghyEventDetail';
import { ERROR_MESSAGE, NO_EVENTS_FOUND } from './dinghyEvents.message';

interface IViewState {
  filter: string;
  sort: string;
}

export interface IDinghyEvent {
  org?: string;
  repo?: string;
  commits?: string[];
  files?: string[];
  date?: number;
  message?: string;
  status?: string;
  rawdata?: string;
  rendereddinghyfile?: string;
  pullrequest?: string;
}

export interface IDinghyEventsPagination {
  currentPage: number;
  itemsPerPage: number;
  maxSize: number;
}

export interface IDinghyEventsState {
  events: IDinghyEvent[];
  pagination: IDinghyEventsPagination;
  errorState: boolean;
  eventSelected: IDinghyEvent;
  ref: React.RefObject<SplitterLayout>;
}

export class DinghyEvents extends React.Component<{}, IDinghyEventsState> {

  private filter$ = new BehaviorSubject<string>('');
  private pagination$ = new BehaviorSubject<IDinghyEventsPagination>(this.getDefaultPagination());
  private sort$ = new BehaviorSubject<string>(null);
  private destroy$ = new Subject();

  constructor(props: {}) {
    super(props);
    const pagination = this.getDefaultPagination();
    const ref = React.createRef();
    this.state = { pagination: pagination, ref: ref } as IDinghyEventsState;
  }

  public componentWillUnmount(): void {
    this.destroy$.next();
  }

  public componentDidMount() {
    const viewState: IViewState = { filter: '', sort: '-date' };
    this.filter$.next(viewState.filter);
    this.sort$.next(viewState.sort);

    const eventsMatchesQuery = (query: string, app: IDinghyEvent) => {
      const searchableValues = [app.commits, app.rawdata, app.message, app.repo, app.org]
        .filter((f) => !!f)
        .map((f) => f.toString().toLowerCase());
      return searchableValues.some((value) => value.includes(query));
    };

    const eventSort = (column: string, a: any, b: any) => {
      const reverse = column[0] === '-';
      const key = reverse ? column.slice(1) : column;
      return (a[key] || '').toString().localeCompare(b[key] || '') * (reverse ? -1 : 1);
    };


    const filteredEvents$ = combineLatest([
      from(DinghyEventsService.getEvents()),
      this.filter$,
      this.sort$
    ])
      .pipe(
      // Apply filter/sort
      map(([events, filter, sort]) => {
        return events.filter((event) => eventsMatchesQuery(filter, event)).sort((a, b) => eventSort(sort, a, b));
      }),
    );


    combineLatest(
      filteredEvents$,
      this.pagination$.pipe(distinctUntilChanged(isEqual)),
    ).pipe(
      // validate and update pagination
      map(([events, pagination]) => {
        const lastPage = Math.floor(events.length / pagination.itemsPerPage) + 1;
        const currentPage = Math.min(pagination.currentPage, lastPage);
        const maxSize = events.length;
        const validatedPagination = { ...pagination, currentPage, maxSize } as IDinghyEventsPagination;
        return { events, pagination: validatedPagination };
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      ({ events, pagination }) => {
        const { currentPage, itemsPerPage } = pagination;
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        this.setState({ events: events.slice(start, end), pagination });
      },
      (error) => {
        this.setState({ errorState: true });
        throw error;
      },
    );
  }

  private toggleSort(column: string): void {
    const current = this.sort$.getValue();
    const newSort = current === column ? `-${column}` : column;
    this.sort$.next(newSort);
  }

  private selectEvent(event: IDinghyEvent): void {
    this.setState({ eventSelected: event });
    // takes up the bottom 50% of the screen when an event is opened
    this.state.ref.current.setState({ secondaryPaneSize: 50 });
  }

  private getDefaultPagination(): IDinghyEventsPagination {
    return {
      currentPage: 1,
      itemsPerPage: 7,
      maxSize: 7,
    };
  }

  public render() {
    const { events, pagination, errorState, eventSelected, ref } = this.state;
    const { maxSize, currentPage, itemsPerPage } = pagination;
    const currentSort = this.sort$.value;
    const changePage: SelectCallback = (page: any) => {
      return this.pagination$.next({ ...pagination, currentPage: page });
    };

    const LoadingSpinner = () => (
      <div className="horizontal middle center" style={{ marginBottom: '250px', height: '150px' }}>
        <Spinner size="medium"/>
      </div>
    );

    const ErrorIndicator = () => (
      <div className="horizontal middle center heading-4" style={{ marginBottom: '250px', height: '150px' }}>
        <i className="fa fa-exclamation-triangle" style={{ paddingRight: '8px' }}/>
        <span>
          {ERROR_MESSAGE}
        </span>
      </div>
    );

    const DinghyData = () => {

      if (errorState) {
        return <ErrorIndicator/>;
      }

      if (!events) {
        return <LoadingSpinner/>;
      }

      if (events.length === 0) {
        if (this.filter$.value.length === 0) {
          return <h4>{NO_EVENTS_FOUND}</h4>;
        }
        return <h4>No matches found for '{this.filter$.value}'</h4>;
      }

      return (
        <div className="infrastructure-section">
          <DinghyEventsTable
            events={events}
            currentSort={currentSort}
            toggleSort={(column) => this.toggleSort(column)}
            selectEvent={(e) => this.selectEvent(e)}
          />
          <PaginationControls
            onPageChanged={changePage}
            activePage={currentPage}
            totalPages={Math.ceil(maxSize / itemsPerPage)}
          />
        </div>
      );
    };

    return (
      <div className="infrastructure">

        <SplitterLayout vertical={true} primaryIndex={0} percentage={true} primaryMinSize={25} secondaryInitialSize={15} secondaryMinSize={5} ref={ref}>
          <div>
            <div className="infrastructure-section search-header">
              <div className="container">
                <h2 className="header-section">
                  <span className="search-label">Dinghy Events</span>
                  <input
                    type="search"
                    placeholder="Search events"
                    className="form-control input-md"
                    ref={(input) => input && input.focus()}
                    onChange={(evt) => this.filter$.next(evt.target.value)}
                    value={this.filter$.value}
                  />
                </h2>
              </div>
            </div>
            <div className="container">
              <DinghyData/>
            </div>
          </div>
          <div className="container">
            <DinghyEventDetail eventSelected={eventSelected} errorState={errorState} events={events}/>
          </div>
        </SplitterLayout>
      </div>
    );
  }
}
